/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useCallback, useState } from 'react';
import debounce from 'lodash/debounce';
import { Search as SearchKit, SEARCH_MODE } from '@veeam/components/lib/controls/Search';

import type { SearchProps as SearchPropsKit } from '@veeam/components/lib/controls/Search';

import { useGridApi } from '../Grid/hooks';

export interface SearchProps extends SearchPropsKit {
    name?: string;
    debounceDelay?: number;
}

export const Search = (props: SearchProps) => {
    const { name, maxLength = 512, ...propsForSearchKit } = props;
    const gridApi = useGridApi();
    const [value, setValue] = useState(gridApi.filters[name] as string || '');

    const onSearch = useCallback(debounce((value: string) => {
        gridApi.applyFilters({ [name]: value });
    }, props.debounceDelay || 500), [gridApi, name]);

    const onClear = useCallback(() => {
        gridApi.applyFilters({ [name]: '' });
    }, [gridApi, name]);

    if (gridApi && name) { // search field inside our grid
        const searchMode = props.mode ?? SEARCH_MODE.automatic; // historically we use automatic mode by default, but kit team uses compulsory one

        if (searchMode === SEARCH_MODE.automatic) {
            return (
                <SearchKit
                    value={gridApi.filters[name] as string || ''}
                    onChange={onSearch}
                    onSearch={onSearch}
                    onClear={onClear}
                    maxLength={maxLength}
                    {...propsForSearchKit}
                    mode={searchMode}
                />
            );
        }

        if (searchMode === SEARCH_MODE.compulsory) {
            return (
                <SearchKit
                    value={value}
                    onChange={setValue}
                    onSearch={onSearch}
                    onClear={onClear}
                    maxLength={maxLength}
                    {...propsForSearchKit}
                    mode={searchMode}
                />
            );
        }

    } else {
        return <SearchKit maxLength={maxLength} {...propsForSearchKit} />;
    }
};
